import React from 'react';
// import { Link } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import aetna from '../assets/insuranceLogos/aetna-seeklogo.svg';
import ambetter from '../assets/insuranceLogos/ambetter.png';
import anthem from '../assets/insuranceLogos/anthem-seeklogo.svg';
import bcbs from '../assets/insuranceLogos/blue-cross-blue-shield-seeklogo.png';
import humana from '../assets/insuranceLogos/Humana_logo.svg';
import magellan from '../assets/insuranceLogos/magellan-health-seeklogo.png';
import medicare from '../assets/insuranceLogos/medicare-seeklogo.svg';
import molina from '../assets/insuranceLogos/molina-healthcare-seeklogo.png';
import optum from '../assets/insuranceLogos/optum-seeklogo.svg';
import oscar from '../assets/insuranceLogos/oscar-health-seeklogo.png';
import curative from '../assets/insuranceLogos/curative-seeklogo.png';
import tricare from '../assets/insuranceLogos/tricare-seeklogo.svg';
import uhc from '../assets/insuranceLogos/unitedhealthcare-seeklogo.svg';
import wellcare from '../assets/insuranceLogos/wellcare-seeklogo.png';

const zocdocLink =
  'https://www.zocdoc.com/practice/aspire-bhp-179924?lock=true&isNewPatient=false&referrerType=widget';

const reasons = [
  {
    title: 'Personalized Psychiatric Care',
    description:
      'We take time to understand your symptoms, concerns, and goals before creating a treatment plan. Your care may include psychiatric evaluations, medication management, therapy coordination, or advanced treatment options.',
  },
  {
    title: 'Compassionate Mental Health Providers',
    description:
      'Our experienced providers offer supportive, non-judgmental care for anxiety, depression, ADHD, mood disorders, PTSD, and other mental health concerns.',
  },
  {
    title: 'In-Person & Telehealth Appointments',
    description:
      'Aspire Behavioral Health offers flexible mental health care with in-person appointments and secure telehealth psychiatry options for patients in Texas and Minnesota.',
  },
  {
    title: 'Evidence-Based Treatment Options',
    description:
      'We use research-backed treatments including medication management, TMS therapy, SPRAVATO® treatment, ADHD testing, and Suboxone® treatment for opioid use disorder.',
  },
];

const insurances = [
  { name: 'Aetna', logo: aetna },
  { name: 'Ambetter', logo: ambetter },
  { name: 'Anthem', logo: anthem },
  { name: 'Blue Cross and Blue Shield', logo: bcbs },
  { name: 'Humana', logo: humana },
  { name: 'Magellan', logo: magellan },
  { name: 'Medicare', logo: medicare },
  { name: 'Molina Healthcare', logo: molina },
  { name: 'Optum', logo: optum },
  { name: 'Oscar Health', logo: oscar },
  { name: 'Curative', logo: curative },
  { name: 'TRICARE', logo: tricare },
  { name: 'UnitedHealthcare / UHC / UBH', logo: uhc },
  { name: 'WellCare', logo: wellcare },
];

const steps = [
  {
    title: 'Schedule Your Appointment',
    description:
      'Request an appointment online or call our office to get started with psychiatric care, medication management, ADHD testing, or other mental health services.',
  },
  {
    title: 'Complete Your Initial Evaluation',
    description:
      'Your provider will review your symptoms, mental health history, current concerns, and treatment goals during a comprehensive psychiatric evaluation.',
  },
  {
    title: 'Receive a Personalized Treatment Plan',
    description:
      'We create a care plan designed around your needs, which may include medication management, therapy coordination, telehealth appointments, TMS therapy, or SPRAVATO® treatment.',
  },
  {
    title: 'Continue With Ongoing Support',
    description:
      'Follow-up appointments help us monitor progress, adjust medications if needed, and support your long-term mental health and wellness.',
  },
];

const faqs = [
  {
    question: 'Do you accept insurance?',
    answer:
      'Yes. Aspire Behavioral Health accepts many major insurance plans. Coverage can vary by plan and service, so we recommend calling our office to verify your benefits before your appointment.',
  },
  {
    question: 'Do you offer telehealth appointments?',
    answer:
      'Yes. We offer secure telehealth psychiatry appointments for eligible patients in Texas and Minnesota.',
  },
  {
    question: 'What mental health conditions do you treat?',
    answer:
      'We treat anxiety, depression, ADHD, bipolar disorder, PTSD, mood disorders, OCD, opioid use disorder, and other mental health concerns.',
  },
  {
    question: 'Do you provide medication management?',
    answer:
      'Yes. Our providers offer psychiatric evaluations and medication management to help create a treatment plan based on your symptoms, history, and goals.',
  },
  {
    question: 'Do you offer ADHD testing?',
    answer:
      'Yes. Aspire Behavioral Health offers ADHD testing and assessments, including QbTest® and Creyos assessments.',
  },
  {
    question: 'How do I schedule an appointment?',
    answer:
      'You can call 210-866-9850 or request an appointment online through our scheduling page.',
  },
];

const WhyChooseUs = () => (
  <Box
    sx={{
      backgroundColor: '#f9faf7',
      py: { xs: 5, md: 8 },
      px: { xs: 2, sm: 3 },
      textAlign: 'center',
      width: '100%',
      maxWidth: '100vw',
      overflowX: 'hidden',
      boxSizing: 'border-box',
    }}
  >
    {/* Why Choose Us */}
    <Typography
      variant="h4"
      sx={{
        fontWeight: 700,
        mb: 2,
        color: '#2e3a11',
        fontSize: { xs: '2rem', md: '2.6rem' },
      }}
    >
      Why Choose Aspire Behavioral Health?
    </Typography>

    <Typography
      variant="body1"
      sx={{
        maxWidth: '900px',
        mx: 'auto',
        lineHeight: 1.9,
        mb: 6,
        color: '#4b4b4b',
        fontSize: { xs: '1rem', sm: '1.1rem' },
      }}
    >
      We believe mental health care should feel supportive, approachable, and
      personalized. Our team provides psychiatric care, medication management,
      ADHD testing, depression treatment, anxiety treatment, and advanced mental
      health services designed around your needs.
    </Typography>

    <Grid container spacing={4} justifyContent="center">
      {reasons.map((reason) => (
        <Grid
          item
          xs={12}
          sm={6}
          md={5}
          key={reason.title}
          sx={{ display: 'flex' }}
        >
          <Card
            sx={{
              width: '100%',
              minHeight: 260,
              backgroundColor: '#eef3e6',
              borderRadius: 4,
              border: '1px solid rgba(58,76,23,0.10)',
              boxShadow: '0px 4px 12px rgba(0,0,0,0.06)',
              transition: '0.2s ease',
              display: 'flex',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0px 6px 18px rgba(0,0,0,0.10)',
              },
            }}
          >
            <CardContent
              sx={{
                p: 4,
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                textAlign: 'center',
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  mb: 2,
                  color: '#2e3a11',
                }}
              >
                {reason.title}
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  lineHeight: 1.9,
                  color: '#4b4b4b',
                }}
              >
                {reason.description}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>

    {/* Insurance Accepted */}
    <Box
      sx={{
        maxWidth: '1100px',
        mx: 'auto',
        mt: { xs: 7, md: 9 },
        p: { xs: 3, md: 5 },
        borderRadius: 4,
        backgroundColor: '#eef3e6',
        border: '1px solid rgba(58,76,23,0.10)',
        textAlign: 'center',
      }}
    >
      <Typography
        variant="h4"
        sx={{
          fontWeight: 700,
          mb: 2,
          color: '#2e3a11',
          fontSize: { xs: '2rem', md: '2.5rem' },
        }}
      >
        Insurances Accepted
      </Typography>

      <Typography
        variant="body1"
        sx={{
          maxWidth: '850px',
          mx: 'auto',
          mb: 4,
          lineHeight: 1.9,
          color: '#4b4b4b',
          fontSize: { xs: '1rem', sm: '1.1rem' },
        }}
      >
        Aspire Behavioral Health accepts many major insurance plans for
        psychiatric care, medication management, telehealth psychiatry, ADHD
        testing, depression treatment, anxiety treatment, and other mental health
        services. If your insurance is not listed, please call us. We may still
        be able to verify your coverage or discuss available options.
      </Typography>

      <Grid container spacing={3} justifyContent="center">
        {insurances.map(({ name, logo }) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={name}>
            <Card
              sx={{
                height: '100%',
                borderRadius: 3,
                boxShadow: '0px 2px 8px rgba(0,0,0,0.08)',
                backgroundColor: 'white',
                transition: '0.2s ease',
                '&:hover': {
                  transform: 'translateY(-3px)',
                  boxShadow: '0px 5px 14px rgba(0,0,0,0.14)',
                },
              }}
            >
              <CardContent
                sx={{
                  minHeight: 130,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  textAlign: 'center',
                  p: 3,
                }}
              >
                <Box
                  component="img"
                  src={logo}
                  alt={`${name} insurance logo`}
                  sx={{
                    height: 45,
                    width: '100%',
                    maxWidth: 120,
                    objectFit: 'contain',
                    mb: 2,
                  }}
                />

                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    color: '#333',
                    lineHeight: 1.4,
                  }}
                >
                  {name}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>

    {/* What to Expect */}
    <Box
      sx={{
        maxWidth: '1200px',
        mx: 'auto',
        mt: { xs: 7, md: 9 },
        textAlign: 'center',
      }}
    >
      <Typography
        variant="h4"
        sx={{
          fontWeight: 700,
          mb: 2,
          color: '#2e3a11',
          fontSize: { xs: '2rem', md: '2.5rem' },
        }}
      >
        What to Expect
      </Typography>

      <Typography
        variant="body1"
        sx={{
          maxWidth: '850px',
          mx: 'auto',
          mb: 5,
          lineHeight: 1.9,
          color: '#4b4b4b',
          fontSize: { xs: '1rem', sm: '1.1rem' },
        }}
      >
        Starting mental health treatment can feel overwhelming, so we make the
        process simple, supportive, and easy to understand from your first
        appointment to ongoing follow-up care.
      </Typography>

      <Grid container spacing={4} justifyContent="center">
        {steps.map((step, index) => (
          <Grid
            item
            xs={12}
            sm={6}
            md={3}
            key={step.title}
            sx={{ display: 'flex' }}
          >
            <Card
              sx={{
                width: '100%',
                minHeight: 310,
                backgroundColor: '#eef3e6',
                borderRadius: 4,
                border: '1px solid rgba(58,76,23,0.10)',
                boxShadow: '0px 4px 12px rgba(0,0,0,0.06)',
                display: 'flex',
              }}
            >
              <CardContent
                sx={{
                  p: 3,
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  textAlign: 'center',
                }}
              >
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: '50%',
                    backgroundColor: '#dfe8d3',
                    color: '#2e3a11',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 800,
                    mb: 2,
                  }}
                >
                  {index + 1}
                </Box>

                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    mb: 2,
                    color: '#2e3a11',
                  }}
                >
                  {step.title}
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    lineHeight: 1.8,
                    color: '#4b4b4b',
                  }}
                >
                  {step.description}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>

    {/* FAQ */}
    <Box
      sx={{
        maxWidth: '900px',
        mx: 'auto',
        mt: { xs: 7, md: 9 },
        textAlign: 'center',
      }}
    >
      <Typography
        variant="h4"
        sx={{
          fontWeight: 700,
          mb: 2,
          color: '#2e3a11',
          fontSize: { xs: '2rem', md: '2.5rem' },
        }}
      >
        Frequently Asked Questions
      </Typography>

      <Typography
        variant="body1"
        sx={{
          maxWidth: '750px',
          mx: 'auto',
          mb: 4,
          lineHeight: 1.8,
          color: '#4b4b4b',
        }}
      >
        Learn more about appointments, insurance, telehealth psychiatry,
        medication management, ADHD testing, and mental health services at
        Aspire Behavioral Health.
      </Typography>

      {faqs.map((faq) => (
        <Accordion
          key={faq.question}
          sx={{
            mb: 2,
            borderRadius: '14px !important',
            backgroundColor: '#eef3e6',
            boxShadow: '0px 3px 10px rgba(0,0,0,0.05)',
            '&:before': {
              display: 'none',
            },
          }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography
              sx={{
                fontWeight: 700,
                color: '#2e3a11',
                textAlign: 'left',
              }}
            >
              {faq.question}
            </Typography>
          </AccordionSummary>

          <AccordionDetails>
            <Typography
              sx={{
                color: '#4b4b4b',
                lineHeight: 1.8,
                textAlign: 'left',
              }}
            >
              {faq.answer}
            </Typography>
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>

    {/* Final CTA */}
    <Box sx={{ textAlign: 'center', mt: { xs: 7, md: 9 } }}>
      <Typography
        variant="h5"
        sx={{
          fontWeight: 700,
          mb: 1,
          color: '#2e3a11',
        }}
      >
        Ready to take the next step?
      </Typography>

      <Typography
        variant="body1"
        sx={{
          maxWidth: '650px',
          mx: 'auto',
          mb: 3,
          lineHeight: 1.8,
          color: '#4b4b4b',
        }}
      >
        Schedule an appointment with Aspire Behavioral Health for compassionate
        psychiatric care and mental health treatment.
      </Typography>

      <Box
        sx={{
          display: 'flex',
          gap: 2,
          justifyContent: 'center',
          flexWrap: 'wrap',
        }}
      >
        <Button
          variant="contained"
          href="tel:2108669850"
          sx={primaryButtonStyle}
        >
          Call 210-866-9850
        </Button>

        <Button
          component="a"
          href={zocdocLink}
          target="_blank"
          rel="noopener noreferrer"
          variant="outlined"
          sx={secondaryButtonStyle}
        >
          Schedule an Appointment
        </Button>
      </Box>
    </Box>
  </Box>
);

const primaryButtonStyle = {
  backgroundColor: '#dfe8d3',
  color: '#2e3a11',
  fontWeight: 700,
  px: 4,
  py: 1.3,
  borderRadius: '30px',
  textTransform: 'none',
  boxShadow: 'none',
  '&:hover': {
    backgroundColor: '#d3dfc3',
    boxShadow: 'none',
  },
};

const secondaryButtonStyle = {
  backgroundColor: 'transparent',
  border: '2px solid #3a4c17',
  color: '#3a4c17 !important',
  fontWeight: 700,
  px: 4,
  py: 1.3,
  borderRadius: '30px',
  textTransform: 'none',
  '&:visited': {
    color: '#3a4c17',
  },
  '&:hover': {
    backgroundColor: 'rgba(58,76,23,0.05)',
    border: '2px solid #3a4c17',
    color: '#3a4c17',
  },
};

export default WhyChooseUs;